import React, { useState, useEffect, useRef } from "react";
import { Form, Popconfirm } from "antd";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";

import Button from "@/common/elements/Button";
import Modal from "@/common/elements/Modal";
import Table from "@/common/elements/Table";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import CommonDatePicker from "@/common/elements/DatePicker";
import TimePicker from "@/common/elements/TimePicker";
import Checkbox from "antd/es/checkbox/Checkbox";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import DynamicForm from "@/common/DynamicForm";

import notificationService from "@/util/notificationService";
import {
  getOptionsFromRefList,
  hasCheckSecurity,
  isNonEmptyVariable,
  convertApiFields,
  getSQLTypeToHTMLType,
} from "@/util/common_function";
import { findNextOccurrence } from "@/util/scheduledEventMovementAutoCreate";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import api from "@/services/axios_custom";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";
import {
  StyledGridCol,
  StyledGridRow,
  StyledTypographyText,
} from "@/common/styles";
import { BookingsSectionTitle } from "./styles";
import ScheduledEventsInspections from "../ScheduledEventsInspections";
import API_ENDPOINTS from "@/util/api";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SCHEMA_NAME,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const DAYS_OF_WEEK = [
  "Sunday", "Monday", "Tuesday", "Wednesday",
  "Thursday", "Friday", "Saturday",
];

const SCHEDULED_EVENTS_HEADERS = [
  { key: "eventTypeLabel",    text: "Event Type"          },
  { key: "eventDescription",  text: "Description"         },
  { key: "displayRecurring",  text: "Recurring"           },
  { key: "LastScheduledDate", text: "Last Scheduled Date" },
  { key: "displayStartDate",  text: "Start Date"          },
  { key: "displayEndDate",    text: "End Date"            },
  { key: "businessName",      text: "Business Name"       },
  { key: "comments",          text: "Comments"            },
];

const ScheduledEventsDetails = (startingPoint) => {
  const dispatch = useDispatch();
  const { bookings, bookingsFieldsEnabled } = useSelector((s) => s.bookings);
  const { userDetail } = useSelector((s) => s?.userDetail);
  const fullSearch = useSelector((s) => s.fullSearch);

  const [inspections, setInspections]               = useState([]);
  const [localScheduledEvents, setLocalScheduledEvents] = useState([]);
  const [showScheduledEvents, setShowScheduledEvents] = useState(false);
  const [modalData, setModalData]                   = useState(null);
  const [isEditing, setIsEditing]                   = useState(false);
  const [isDisabled, setIsDisabled]                 = useState(false);
  const [form]                                      = Form.useForm();

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md).
  const {
    headers: scheduledEventsHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("Bookings:ScheduledEvents", SCHEDULED_EVENTS_HEADERS);

  // -----------------------------------------------------------------------
  // Sub-module security — computed once
  // -----------------------------------------------------------------------
  const canAdd    = hasCheckSecurity(Security_Types.functionality, Entity_types.subModule, LABELS_CAPITAL.ADD,    MODULES_LIST_WITH_ID.MODULE_BOOKINGS, SUB_MODULES_LIST_WITH_ID.Arrest_Report);
  const canView   = hasCheckSecurity(Security_Types.functionality, Entity_types.subModule, LABELS_CAPITAL.VIEW,   MODULES_LIST_WITH_ID.MODULE_BOOKINGS, SUB_MODULES_LIST_WITH_ID.Arrest_Report);
  const canModify = hasCheckSecurity(Security_Types.functionality, Entity_types.subModule, LABELS_CAPITAL.MODIFY, MODULES_LIST_WITH_ID.MODULE_BOOKINGS, SUB_MODULES_LIST_WITH_ID.Arrest_Report);
  const canDelete = hasCheckSecurity(Security_Types.functionality, Entity_types.subModule, LABELS_CAPITAL.DELETE, MODULES_LIST_WITH_ID.MODULE_BOOKINGS, SUB_MODULES_LIST_WITH_ID.Arrest_Report);

  // -----------------------------------------------------------------------
  // Reference data
  // -----------------------------------------------------------------------
  const daysOfWeekOptions    = getOptionsFromRefList("DaysOfWeek", false, false, true);
  const scheduledEventOptions = getOptionsFromRefList("Scheduled_Event_Type", false, false, true);

  const dayOfWeekMap = daysOfWeekOptions.reduce((acc, { value, label }) => {
    acc[value] = label;
    return acc;
  }, {});

  const getDayOfWeekID = (dayName) => {
    const entry = Object.entries(dayOfWeekMap).find(([, name]) => name === dayName);
    return entry ? parseInt(entry[0], 10) : null;
  };

  const getScheduledEventKey = (item) =>
    item?.scheduledEventID ?? item?.id;

  // -----------------------------------------------------------------------
  // Form.useWatch
  // -----------------------------------------------------------------------
  const isRecurring       = Form.useWatch("recurring",             form);
  const isActiveRecurring = Form.useWatch("activeRecurring",       form);
  const eventTypeValue    = Form.useWatch("scheduledEventTypeID",  form);
  const existingBusIdValue = Form.useWatch("existingBusId",        form);

  // -----------------------------------------------------------------------
  // BusinessesExtra — PCI's per-client dynamic "_U" columns for the Job
  // Schedule business (BusinessInterface.asp's "Additional Business
  // Information" section, shown right after the business address fields).
  // -----------------------------------------------------------------------
  const [businessExtraFields, setBusinessExtraFields] = useState([]);
  const [businessExtraFieldsReady, setBusinessExtraFieldsReady] = useState(false);
  const businessExtraTypeMapRef = useRef({});
  const businessExtraIdRef = useRef(0);

  useEffect(() => {
    api
      .get(`${API_ENDPOINTS.getTableColumns}/BusinessesExtra`)
      .then((response) => {
        const tableData = (response?.data || []).filter((d) =>
          d?.columnName?.endsWith("_U")
        );
        const converted = convertApiFields(tableData, "BusinessesExtra");
        const typeMap = {};
        converted.forEach((c) => {
          typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
        });
        businessExtraTypeMapRef.current = typeMap;
        setBusinessExtraFields(converted);
        setBusinessExtraFieldsReady(true);
      })
      .catch((error) => {
        console.error(
          "[ScheduledEventsDetails] BusinessesExtra field defs fetch failed:",
          error
        );
      });
  }, []);

  // Fetches the saved BusinessesExtra row when editing a Job Schedule event
  // whose business is already saved — a brand new business has no
  // businessId yet.
  useEffect(() => {
    if (!businessExtraFieldsReady) return;
    if (!isNonEmptyVariable(existingBusIdValue)) {
      businessExtraIdRef.current = 0;
      return;
    }

    fetchExtraTableRecord("BusinessesExtra", "businessID", existingBusIdValue)
      .then((result) => {
        if (!result) return;
        businessExtraIdRef.current = result.raw?.BusinessExtraID ?? 0;
        const withDates = Object.keys(result.values).reduce((acc, key) => {
          const val = result.values[key];
          const htmlType = businessExtraTypeMapRef.current[key];
          const isDateField = htmlType === "date" || htmlType === "datetime";
          acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
          return acc;
        }, {});
        form.setFieldsValue(withDates);
      })
      .catch((error) => {
        console.error(
          "[ScheduledEventsDetails] BusinessesExtra fetch failed:",
          error
        );
      });
  }, [existingBusIdValue, businessExtraFieldsReady]);

  // -----------------------------------------------------------------------
  // ScheduledEventsExtra — PCI's per-client dynamic "_U" columns for this
  // event (ScheduledEventsInterface.asp's "Additional Event Information"
  // section, shown right after Contact Name, before One-Time/Recurring
  // Event Information). Replaces the previous hardcoded "Sign Out Inmate"
  // checkbox, which only happened to match this table's one column for one
  // particular client's config — other clients (like the one in this
  // screenshot) have different/additional _U columns.
  // -----------------------------------------------------------------------
  const [scheduledEventsExtraFields, setScheduledEventsExtraFields] = useState([]);
  const [scheduledEventsExtraFieldsReady, setScheduledEventsExtraFieldsReady] =
    useState(false);
  const scheduledEventsExtraTypeMapRef = useRef({});
  const scheduledEventsExtraIdRef = useRef(0);
  // Only a real, previously-saved event has this — a locally-added-but-
  // unsaved event only has a client-generated "new-<timestamp>" id, not
  // this. Set directly in handleEdit/clickScheduledEvents below (not a
  // watched form field, since the old scheduledEventID hidden field this
  // used to read is gone along with the hardcoded checkbox it supported).
  const [currentScheduledEventId, setCurrentScheduledEventId] = useState(null);

  useEffect(() => {
    api
      .get(`${API_ENDPOINTS.getTableColumns}/ScheduledEventsExtra`)
      .then((response) => {
        const tableData = (response?.data || []).filter((d) =>
          d?.columnName?.endsWith("_U")
        );
        const converted = convertApiFields(tableData, "ScheduledEventsExtra");
        const typeMap = {};
        converted.forEach((c) => {
          typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
        });
        scheduledEventsExtraTypeMapRef.current = typeMap;
        setScheduledEventsExtraFields(converted);
        setScheduledEventsExtraFieldsReady(true);
      })
      .catch((error) => {
        console.error(
          "[ScheduledEventsDetails] ScheduledEventsExtra field defs fetch failed:",
          error
        );
      });
  }, []);

  // Fetches the saved ScheduledEventsExtra row when editing an existing
  // event — a brand new event has no scheduledEventID yet.
  useEffect(() => {
    if (!scheduledEventsExtraFieldsReady) return;
    if (!isNonEmptyVariable(currentScheduledEventId)) {
      scheduledEventsExtraIdRef.current = 0;
      return;
    }

    fetchExtraTableRecord("ScheduledEventsExtra", "scheduledEventID", currentScheduledEventId)
      .then((result) => {
        if (!result) return;
        scheduledEventsExtraIdRef.current = result.raw?.ScheduledEventExtraID ?? 0;
        const withDates = Object.keys(result.values).reduce((acc, key) => {
          const val = result.values[key];
          const htmlType = scheduledEventsExtraTypeMapRef.current[key];
          const isDateField = htmlType === "date" || htmlType === "datetime";
          acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
          return acc;
        }, {});
        form.setFieldsValue(withDates);
      })
      .catch((error) => {
        console.error(
          "[ScheduledEventsDetails] ScheduledEventsExtra fetch failed:",
          error
        );
      });
  }, [currentScheduledEventId, scheduledEventsExtraFieldsReady]);

  const selectedEventTypeLabel = scheduledEventOptions.find(
    (opt) => opt.value === eventTypeValue
  )?.label;
  const isJobSchedule = selectedEventTypeLabel === "Job Schedule";

  // -----------------------------------------------------------------------
  // Effects
  // -----------------------------------------------------------------------
  useEffect(() => {
    setLocalScheduledEvents(bookings?.scheduledEvents || []);
  }, [bookings?.scheduledEvents]);

  useEffect(() => {
    if (isJobSchedule) form.setFieldsValue({ recurring: true });
  }, [isJobSchedule, form]);

  useEffect(() => {
    setIsDisabled(isEditing ? !canModify : false);
  }, [isEditing]);

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------
  const clickScheduledEvents = () => {
    setModalData(null);
    setIsEditing(false);
    setShowScheduledEvents(true);
    setInspections([]);
    // A stale currentScheduledEventId from a previously-viewed row would
    // otherwise make the ScheduledEventsExtra fetch effect above populate
    // this fresh Add form with the old event's extra values.
    setCurrentScheduledEventId(null);
    scheduledEventsExtraIdRef.current = 0;
    form.resetFields();
    form.setFieldsValue({
      oneTimeDateTime: "",
      startDate: !fullSearch ? dayjs() : null,
    });
  };

  const handleCloseModal = () => {
    setShowScheduledEvents(false);
    setModalData(null);
    setIsEditing(false);
    form.resetFields();
  };

  const onRowClick = (row) => setModalData(row);

  const handleEdit = (row) => {
    const data = row?.scheduledEventID || row?.id ? row : modalData;
    if (!data) {
      notificationService.warning("Please select a row to edit.");
      return;
    }
    if (row?.scheduledEventID || row?.id) setModalData(row);
    setCurrentScheduledEventId(data?.scheduledEventID || null);

    const combinedDateTime =
      data?.oneTimeDate && data?.oneTime
        ? dayjs(`${data.oneTimeDate} ${data.oneTime}`, "MM/DD/YYYY HH:mm")
        : null;

    const recurringData = data?.scheduledEventsRecurring?.[0] || {};
    const currentEventTypeLabel = scheduledEventOptions.find(
      (opt) => opt.value === data?.scheduledEventTypeID
    )?.label;
    const isActuallyJobSchedule = currentEventTypeLabel === "Job Schedule";

    const startDateVal       = recurringData.startDate       ? dayjs(recurringData.startDate,       "MM/DD/YYYY") : null;
    const endDateVal         = recurringData.endDate         ? dayjs(recurringData.endDate,         "MM/DD/YYYY") : null;
    const reactivationDateVal = recurringData.reactivationDate ? dayjs(recurringData.reactivationDate, "MM/DD/YYYY") : null;

    const daysObj = DAYS_OF_WEEK.reduce((acc, day) => {
      acc[day] = { active: false, startTime: null, endTime: null };
      return acc;
    }, {});

    recurringData?.scheduledEventsRecurringSchedule?.forEach((sched) => {
      const dayName = dayOfWeekMap[parseInt(sched.dayofWeekID, 10)];
      if (dayName && daysObj[dayName]) {
        daysObj[dayName] = {
          active:    !!sched.active,
          startTime: sched.startTime ? dayjs(sched.startTime, "HH:mm") : null,
          endTime:   sched.endTime   ? dayjs(sched.endTime,   "HH:mm") : null,
        };
      }
    });

    let businessNameVal = "", businessHouseNumberVal = "", businessStreetVal = "";
    let businessStreet2Val = "", businessAptVal = "", businessCityVal = "";
    let businessStateVal = "", businessZipVal = "", businessPhoneVal = "", businessFaxVal = "";
    let existingWRDId = null, existingBusId = null, existingBusDetailsId = null, existingAddressId = null;

    if (isActuallyJobSchedule && recurringData.workReleaseDestinations) {
      const wrd    = recurringData.workReleaseDestinations[0] || {};
      const bus    = wrd.businesses?.[0] || {};
      const busDet = bus.businessDetails?.[0] || {};
      const addr   = bus.addresses?.[0] || {};

      existingWRDId         = wrd.workReleaseDestinationID || null;
      existingBusId         = bus.businessId || null;
      existingBusDetailsId  = busDet.businessDetailsId || null;
      existingAddressId     = addr.addressId || null;
      businessNameVal       = bus.businessName || "";
      businessPhoneVal      = bus.businessPhone || "";
      businessFaxVal        = bus.businessFax || "";
      businessHouseNumberVal = addr.addressStreetNumber || "";
      businessStreetVal     = addr.addressStreet1 || "";
      businessStreet2Val    = addr.addressStreet2 || "";
      businessAptVal        = addr.addressApartmentNumber || "";
      businessCityVal       = addr.addressCity || "";
      businessStateVal      = addr.addressState || "";
      businessZipVal        = addr.addressZip || "";
    }

    setInspections(recurringData?.workReleaseDestinations?.[0]?.workReleaseInspections || []);

    form.setFieldsValue({
      ...data,
      activeRecurring:         !!recurringData.active,
      oneTimeDateTime:         combinedDateTime,
      oneTimeEventFinishes:    data?.oneTimeEventFinishes ? dayjs(data.oneTimeEventFinishes, "HH:mm") : null,
      startDate:               startDateVal,
      endDate:                 endDateVal,
      reactivationDate:        reactivationDateVal,
      reactivationComments:    recurringData.reactivationComments || "",
      ...daysObj,
      businessName:            businessNameVal,
      businessHouseNumber:     businessHouseNumberVal,
      businessStreet:          businessStreetVal,
      businessStreet2:         businessStreet2Val,
      businessApt:             businessAptVal,
      businessCity:            businessCityVal,
      businessState:           businessStateVal,
      businessZip:             businessZipVal,
      businessPhone:           businessPhoneVal,
      businessFax:             businessFaxVal,
      existingWRDId, existingBusId, existingBusDetailsId, existingAddressId,
    });

    setIsEditing(true);
    setShowScheduledEvents(true);
  };

  const handleDelete = () => {
    if (!modalData) {
      notificationService.warning("Please select a row to delete.");
      return;
    }
    const keyToDelete = getScheduledEventKey(modalData);
    const updated = localScheduledEvents.filter(
      (item) => getScheduledEventKey(item) !== keyToDelete
    );
    if (typeof keyToDelete === "number") {
      dispatch(setDeleteAPis(API_ENDPOINTS.scheduledEvents + keyToDelete));
    }
    setLocalScheduledEvents(updated);
    dispatch(setBookings({ ...bookings, scheduledEvents: updated }));
    setModalData(null);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // Split BusinessesExtra dynamic fields out of the form values —
      // nested onto businesses[0].businessesExtra below rather than left
      // flat on values, where they'd otherwise leak into the main bookings
      // save payload. See pages/Bookings/index.jsx's strip-before-POST and
      // saveBusinessesExtra.
      const businessExtraFieldKeys = businessExtraFields.map((f) => f.columnName);
      const businessExtraData = {};
      businessExtraFieldKeys.forEach((k) => {
        const val = values?.[k];
        const htmlType = businessExtraTypeMapRef.current[k];
        businessExtraData[k] = val
          ? htmlType === "date"
            ? dayjs(val).format("MM/DD/YYYY")
            : htmlType === "datetime"
              ? dayjs(val).format("MM/DD/YYYY HH:mm")
              : val
          : null;
        delete values[k];
      });

      // Split ScheduledEventsExtra dynamic fields out of the form values —
      // nested onto scheduledEventsExtra below rather than left flat on
      // values, where they'd otherwise leak into the main bookings save
      // payload. See pages/Bookings/index.jsx's strip-before-POST and
      // saveScheduledEventsExtra.
      const scheduledEventsExtraFieldKeys = scheduledEventsExtraFields.map(
        (f) => f.columnName
      );
      const scheduledEventsExtraData = {};
      scheduledEventsExtraFieldKeys.forEach((k) => {
        const val = values?.[k];
        const htmlType = scheduledEventsExtraTypeMapRef.current[k];
        scheduledEventsExtraData[k] = val
          ? htmlType === "date"
            ? dayjs(val).format("MM/DD/YYYY")
            : htmlType === "datetime"
              ? dayjs(val).format("MM/DD/YYYY HH:mm")
              : val
          : null;
        delete values[k];
      });

      let oneTimeDate = null, oneTime = null;
      if (values.oneTimeDateTime) {
        const base = values.travelTime
          ? dayjs(values.oneTimeDateTime).subtract(values.travelTime, "minute")
          : dayjs(values.oneTimeDateTime);
        oneTimeDate = base.format("MM/DD/YYYY");
        oneTime     = base.format("HH:mm");
      }

      const existingScheduledEventExtra = modalData?.scheduledEventsExtra?.[0] || {};
      const scheduledEventsExtra =
        scheduledEventsExtraFieldKeys.length > 0
          ? [
              {
                ...existingScheduledEventExtra,
                ...scheduledEventsExtraData,
                scheduledEventExtraID:
                  existingScheduledEventExtra?.scheduledEventExtraID ??
                  scheduledEventsExtraIdRef.current ??
                  0,
                scheduledEventID:
                  existingScheduledEventExtra?.scheduledEventID ??
                  currentScheduledEventId ??
                  0,
              },
            ]
          : modalData?.scheduledEventsExtra;

      const startDateString       = values.startDate       ? dayjs(values.startDate).format("MM/DD/YYYY")       : null;
      const endDateString         = values.endDate         ? dayjs(values.endDate).format("MM/DD/YYYY")         : null;
      const reactivationDateString = values.reactivationDate ? dayjs(values.reactivationDate).format("MM/DD/YYYY") : null;

      let formattedValues = {
        ...values,
        scheduledEventsExtra,
        oneTimeDate, oneTime,
        oneTimeEventFinishes: values.oneTimeEventFinishes
          ? (values.travelTime
              ? dayjs(values.oneTimeEventFinishes).add(values.travelTime, "minute").format("HH:mm")
              : dayjs(values.oneTimeEventFinishes).format("HH:mm"))
          : null,
        startDate:        startDateString,
        endDate:          endDateString,
        reactivationDate: reactivationDateString,
      };

      let hTOneTimeDate = formattedValues.startDate ?? formattedValues.oneTimeDate;
      let hTBkTimeDate  = formattedValues.startDate ?? formattedValues.oneTimeDate;
      let hTOneTime     = null;
      let hTBkTime      = null;

      delete formattedValues.oneTimeDateTime;

      const existingWRDId        = formattedValues.existingWRDId;
      const existingBusId        = formattedValues.existingBusId;
      const existingBusDetailsId = formattedValues.existingBusDetailsId;
      const existingAddressId    = formattedValues.existingAddressId;
      delete formattedValues.existingWRDId;
      delete formattedValues.existingBusId;
      delete formattedValues.existingBusDetailsId;
      delete formattedValues.existingAddressId;

      let scheduledEventsRecurring = [];
      if (values.recurring) {
        const recurringObj = {
          scheduledEventRecurringID: modalData?.scheduledEventsRecurring?.[0]?.scheduledEventRecurringID || null,
          active:               !!values.activeRecurring,
          startDate:            startDateString,
          endDate:              endDateString,
          reactivationDate:     reactivationDateString,
          reactivationComments: values.reactivationComments || "",
          scheduledEventsRecurringSchedule: [],
        };

        DAYS_OF_WEEK.forEach((dayName) => {
          const dayField = values[dayName];
          if (dayField?.active) {
            recurringObj.scheduledEventsRecurringSchedule.push({
              scheduledEventRecurringScheduleID:
                modalData?.scheduledEventsRecurring?.[0]?.scheduledEventsRecurringSchedule?.find(
                  (r) => getDayOfWeekID(dayName) === r.dayofWeekID
                )?.scheduledEventRecurringScheduleID || null,
              scheduledEventRecurringID: recurringObj.scheduledEventRecurringID,
              active:      true,
              dayofWeekID: getDayOfWeekID(dayName),
              startTime:   dayField.startTime ? dayjs(dayField.startTime).format("HH:mm") : null,
              endTime:     dayField.endTime   ? dayjs(dayField.endTime).format("HH:mm")   : null,
            });
          }
        });

        // Mirrors legacy HandleMovementForRecurringEvent: the next occurrence is the
        // next Active day on/after Start_Date (or today), not just "whichever day
        // happens to come first in Sunday->Saturday order" — lastScheduledDate is left
        // null here so a dialog re-save always searches fresh from Start_Date/today.
        const occurrence = findNextOccurrence({
          scheduledEventsRecurring: [{ ...recurringObj, lastScheduledDate: null }],
        });
        if (occurrence) {
          const extra = values.travelTime || 0;
          hTOneTimeDate = occurrence.date.format("MM/DD/YYYY");
          hTBkTimeDate  = occurrence.date.format("MM/DD/YYYY");
          hTOneTime = occurrence.startTime.subtract(extra, "minute").format("HH:mm");
          hTBkTime  = occurrence.endTime.add(extra, "minute").format("HH:mm");
          recurringObj.lastScheduledDate = occurrence.date.format("MM/DD/YYYY");
        }

        if (isJobSchedule) {
          recurringObj.workReleaseDestinations = [{
            workReleaseDestinationID: existingWRDId || null,
            businessID:               existingBusId || null,
            comments:                 "",
            businesses: [{
              businessId:    existingBusId || null,
              businessName:  values.businessName  || "",
              businessPhone: values.businessPhone || "",
              businessFax:   values.businessFax   || "",
              businessDetails: [{
                businessDetailsId: existingBusDetailsId || null,
                businessId:        existingBusId || null,
                businessPhone:     values.businessPhone || "",
                businessFax:       values.businessFax   || "",
              }],
              addresses: [{
                addressId:              existingAddressId || null,
                addressStreetNumber:    values.businessHouseNumber || "",
                addressStreet1:         values.businessStreet      || "",
                addressStreet2:         values.businessStreet2     || "",
                addressApartmentNumber: values.businessApt         || "",
                addressCity:            values.businessCity        || "",
                addressState:           values.businessState       || "",
                addressZip:             values.businessZip         || "",
              }],
              businessesExtra:
                businessExtraFieldKeys.length > 0
                  ? [{
                      ...businessExtraData,
                      businessExtraID: businessExtraIdRef.current || 0,
                      businessID:      existingBusId || 0,
                    }]
                  : undefined,
            }],
            workReleaseInspections: inspections,
          }];
        }
        scheduledEventsRecurring.push(recurringObj);
      }

      formattedValues.scheduledEventsRecurring = scheduledEventsRecurring;

      if (hTOneTime === null || hTBkTime === null) {
        hTOneTime = formattedValues.oneTime;
        hTBkTime  = formattedValues.oneTimeEventFinishes;
      }

      const rowKey = modalData ? getScheduledEventKey(modalData) : null;
      const combined = [formattedValues.eventDescription, values.comments].filter(Boolean).join(" ");

      // If this event is already linked to a movement, look it up in the flat (authoritative)
      // bookings.inmateTracking array - not the possibly-stale nested copy on modalData - so
      // re-saving the event's schedule details doesn't stomp a status the user already set to
      // Complete/Cancelled from Housing & Tracking back to Pending.
      const linkedInmateTrackingID = modalData?.inmateTracking?.[0]?.inmateTrackingID || null;
      const linkedTracking = linkedInmateTrackingID
        ? (bookings?.inmateTracking || []).find(
            (t) => t?.inmateTrackingID === linkedInmateTrackingID,
          )
        : null;

      const houseTrackData = {
        actualDate:              linkedTracking?.actualDate ?? null,
        actualTime:              linkedTracking?.actualTime ?? null,
        approvingOfficerStaffID: !fullSearch ? userDetail?.staffId : null,
        comments:                !fullSearch ? (combined || null) : null,
        expectedBackDate:        hTBkTimeDate,
        expectedBackTime:        hTBkTime,
        scheduledDate:           hTOneTimeDate,
        scheduledTime:           hTOneTime,
        inmateLocation:          "Loan",
        loanType:                scheduledEventOptions.find((opt) => opt.value === values.scheduledEventTypeID)?.label,
        movementStatus:          linkedTracking?.movementStatus || "Pending",
        bookingId:               bookings.bookingId,
        moveReason:              "Routine Move",
        loanDestination:         "others - See comments",
      };

      let updatedList;
      if (isEditing && rowKey) {
        updatedList = localScheduledEvents.map((item) => {
          if (getScheduledEventKey(item) !== rowKey) return item;
          return {
            ...item,
            ...formattedValues,
            inmateTracking: [{
              ...houseTrackData,
              inmateTrackingID: item?.inmateTracking?.[0]?.inmateTrackingID || null,
            }],
          };
        });
      } else {
        updatedList = [...localScheduledEvents, {
          id: `new-${Date.now()}`,
          inmateTracking: [houseTrackData],
          ...formattedValues,
        }];
      }

      setLocalScheduledEvents(updatedList);
      dispatch(setBookings({ ...bookings, scheduledEvents: updatedList }));
      setShowScheduledEvents(false);
      setModalData(null);
      setIsEditing(false);
      form.resetFields();
    } catch (err) {
      console.error("Form error:", err);
    }
  };

  // -----------------------------------------------------------------------
  // Table data
  // -----------------------------------------------------------------------
  const tableRows = localScheduledEvents?.map((item) => {
    const eventTypeLabel  = scheduledEventOptions.find((opt) => opt.value === item.scheduledEventTypeID)?.label;
    const recurringData   = item?.scheduledEventsRecurring?.[0] || {};
    const businessData    = recurringData?.workReleaseDestinations?.[0]?.businesses?.[0] || {};
    return {
      ...item,
      eventTypeLabel:   eventTypeLabel || "Unknown",
      displayRecurring: item.recurring ? "True" : "False",
      displayStartDate: item.recurring
        ? (recurringData.startDate ? dayjs(recurringData.startDate, "MM/DD/YYYY").format("MM/DD/YYYY") : "-")
        : (item.oneTimeDate ? dayjs(item.oneTimeDate).format("MM/DD/YYYY") : "-"),
      displayEndDate: item.recurring
        ? (recurringData.endDate ? dayjs(recurringData.endDate, "MM/DD/YYYY").format("MM/DD/YYYY") : "-")
        : (item.oneTimeDate ? dayjs(item.oneTimeDate).format("MM/DD/YYYY") : "-"),
      startDate:    recurringData.startDate ? dayjs(recurringData.startDate, "MM/DD/YYYY").format("MM/DD/YYYY") : "-",
      endDate:      recurringData.endDate   ? dayjs(recurringData.endDate,   "MM/DD/YYYY").format("MM/DD/YYYY") : "-",
      businessName: businessData.businessName || "-",
    };
  });

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <>
      {/* ── Toolbar ─────────────────────────────────────────────────── */}
      <StyledGridRow justify="space-between">
        <StyledGridCol>
          <Button onClick={clickScheduledEvents} disabled={!bookingsFieldsEnabled || !canAdd || isConfiguring}>
            ADD
          </Button>
        </StyledGridCol>
        <StyledGridCol>
          <Button
            style={{ marginRight: "10px" }}
            onClick={handleEdit}
            disabled={!modalData || !canView || isConfiguring}
          >
            Details
          </Button>
          <Popconfirm
            title="Delete the Scheduled Event"
            description="Are you sure you want to delete this Scheduled Event?"
            onConfirm={handleDelete}
            onCancel={() => {}}
            okText="Yes"
            cancelText="No"
          >
            <Button disabled={!bookingsFieldsEnabled || !modalData || !canDelete || isConfiguring}>
              Delete
            </Button>
          </Popconfirm>

          {/* Config — drag-reorder/resize columns */}
          {!isConfiguring ? (
            <Button
              style={{ marginLeft: "10px" }}
              htmlType="button"
              onClick={startConfiguring}
            >
              {configButtonLabel}
            </Button>
          ) : (
            <Popconfirm
              title="Do you want to save the current list view configuration?"
              onConfirm={confirmSave}
              onCancel={discardChanges}
              okText="Yes"
              cancelText="No"
            >
              <Button style={{ marginLeft: "10px" }} htmlType="button">
                {configButtonLabel}
              </Button>
            </Popconfirm>
          )}
        </StyledGridCol>
      </StyledGridRow>

      {canView && (
        <Table
          ref={dataTableRef}
          selected={!modalData}
          headers={scheduledEventsHeaders}
          draggable={isConfiguring}
          data={tableRows}
          style={{ marginTop: "15px" }}
          onRowClick={onRowClick}
          onRowDoubleClick={handleEdit}
        />
      )}

      {/* ── Modal ───────────────────────────────────────────────────── */}
      <Modal
        header="Scheduled Events"
        show={showScheduledEvents}
        size="xl"
        onHide={handleCloseModal}
        footerActions={[{ key: "save", text: "Save", onClick: handleSave, disabled: isDisabled }]}
        footerCloseAction={() => setIsEditing(false)}
        footerCloseActionText="Close"
        submitButtonDisabled
      >
        {/* FieldSecurityProvider wraps the entire modal Form */}
        <FieldSecurityProvider
          entity={SCHEMA_NAME.SCHEDULED_EVENTS}
          startingPoint={startingPoint}
          bookingsFieldsEnabled={bookingsFieldsEnabled}
        >
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(changedValues) => {
              if (changedValues.oneTimeDateTime) {
                form.validateFields(["oneTimeEventFinishes"]);
              }
            }}
          >
            {/* ── Scheduled Event Information ──────────────────────── */}
            <BookingsSectionTitle><span>Scheduled Event Information</span></BookingsSectionTitle>
            <StyledGridRow gutter={16}>

              <StyledGridCol span={5}>
                <SecureFormItem
                  fieldName="scheduledEventTypeID"
                  formItemProps={{
                    name: "scheduledEventTypeID", label: "Event Type",
                    required: !fullSearch,
                    rules: !fullSearch ? [{ required: true, message: "Event Type is required" }] : [],
                  }}
                >
                  <Select
                    id="eventType"
                    options={scheduledEventOptions}
                    placeholder="Event Type"
                    showSearch
                    variant="outlined"
                  />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol span={5}>
                <SecureFormItem
                  fieldName="eventDescription"
                  formItemProps={{
                    name: "eventDescription", label: "Description",
                    required: !fullSearch,
                    rules: !fullSearch
                      ? [
                          { required: true, message: "Description is required" },
                          { pattern: /^\S.*\S?$/, message: "Description cannot have empty spaces" },
                        ]
                      : [],
                  }}
                >
                  <TextField />
                </SecureFormItem>
              </StyledGridCol>

              {/* Recurring — overrideDisabled when isJobSchedule forces it true */}
              <StyledGridCol span={4}>
                <SecureFormItem
                  fieldName="recurring"
                  overrideDisabled={isJobSchedule}
                  formItemProps={{
                    name: "recurring", label: "Recurring",
                    layout: "horizontal", labelPostion: "label-right",
                    style: { paddingTop: "25px" }, valuePropName: "checked",
                  }}
                >
                  <Checkbox checked={isJobSchedule || isRecurring} />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol span={5}>
                <SecureFormItem
                  fieldName="travelTime"
                  formItemProps={{
                    name: "travelTime", label: "Travel Time(minutes)",
                    rules: !fullSearch
                      ? [
                          { required: true, message: "Travel Time is required" },
                          { type: "number", transform: (v) => (v ? Math.round(parseFloat(v)) : v), message: "Travel time must be a number." },
                          { validator: (_, v) => v <= 240 ? Promise.resolve() : Promise.reject(new Error("The travel time cannot be longer than 4 hours")) },
                          { pattern: /^\S.*\S?$/, message: "Travel time cannot have empty spaces" },
                        ]
                      : [],
                  }}
                >
                  <TextField
                    onBlur={(e) => {
                      const { value } = e.target;
                      if (value) form.setFieldsValue({ travelTime: Math.round(Number(value)) });
                    }}
                  />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol span={5}>
                <SecureFormItem
                  fieldName="contactName"
                  formItemProps={{ name: "contactName", label: "Contact Name" }}
                >
                  <TextField />
                </SecureFormItem>
              </StyledGridCol>

            </StyledGridRow>

            {/* ── Additional Event Information — ScheduledEventsExtra's
                per-client dynamic "_U" columns (e.g. "Sign Out Inmate"),
                fetched live rather than hardcoded, since these vary by
                client. ─────────────────────────────────────────────── */}
            {scheduledEventsExtraFields.length > 0 && (
              <>
                <BookingsSectionTitle><span>Additional Event Information</span></BookingsSectionTitle>
                <StyledGridRow gutter={16}>
                  {scheduledEventsExtraFields.map((sf, index) => (
                    <DynamicForm
                      key={index}
                      formAttributes={sf}
                      form={form}
                      bookingsFieldsEnabled={bookingsFieldsEnabled}
                    />
                  ))}
                </StyledGridRow>
              </>
            )}
            <StyledGridRow>
              <StyledGridCol span={6}>
                {/* Hidden fields — plain FormItem, no security needed */}
                <FormItem name="existingWRDId"         hidden><input type="hidden" /></FormItem>
                <FormItem name="existingBusId"         hidden><input type="hidden" /></FormItem>
                <FormItem name="existingBusDetailsId"  hidden><input type="hidden" /></FormItem>
                <FormItem name="existingAddressId"     hidden><input type="hidden" /></FormItem>
              </StyledGridCol>
            </StyledGridRow>

            {/* ── One-Time Event ───────────────────────────────────── */}
            {!isRecurring && (
              <>
                <BookingsSectionTitle><span>One-Time Event Information</span></BookingsSectionTitle>
                <StyledGridRow gutter={16}>

                  <StyledGridCol span={6}>
                    <SecureFormItem
                      fieldName="oneTimeDateTime"
                      formItemProps={{
                        name: "oneTimeDateTime", label: "Scheduled Date/Time",
                        required: !fullSearch,
                        rules: !fullSearch ? [{ required: true, message: "Scheduled Date/Time is required" }] : [],
                      }}
                    >
                      <CommonDatePicker
                        placeholder="MM/DD/YYYY HH:mm"
                        format="MM/DD/YYYY HH:mm"
                        showTime={{ format: "HH:mm" }}
                      />
                    </SecureFormItem>
                  </StyledGridCol>

                  <StyledGridCol span={5}>
                    <SecureFormItem
                      fieldName="oneTimeEventFinishes"
                      formItemProps={{
                        name: "oneTimeEventFinishes", label: "Time Event Finishes",
                        dependencies: ["oneTimeDateTime"],
                        rules: !fullSearch
                          ? [({ getFieldValue }) => ({
                              validator(_, finishTimeValue) {
                                const scheduleDateTime = getFieldValue("oneTimeDateTime");
                                if (!scheduleDateTime) return Promise.reject(new Error("Time Event Finish Time is required"));
                                if (!finishTimeValue) {
                                  const selectedEventType = scheduledEventOptions.find(
                                    (opt) => opt.value === getFieldValue("scheduledEventTypeID")
                                  )?.label;
                                  if (["Activity Request", "Medical", "Job Schedule"].includes(selectedEventType)) {
                                    return Promise.reject(new Error("You must enter the end time"));
                                  }
                                }
                                const start = dayjs(scheduleDateTime);
                                const finish = dayjs(`${start.format("MM/DD/YYYY")} ${dayjs(finishTimeValue).format("HH:mm")}`, "MM/DD/YYYY HH:mm");
                                if (!finish.isAfter(start)) return Promise.reject(new Error("The finish time must be later than the scheduled time"));
                                return Promise.resolve();
                              },
                            })]
                          : [],
                      }}
                    >
                      <TimePicker format="HH:mm" placeholder="HH:mm" />
                    </SecureFormItem>
                  </StyledGridCol>

                </StyledGridRow>
              </>
            )}

            {/* ── Recurring Event ──────────────────────────────────── */}
            {isRecurring && (
              <>
                <BookingsSectionTitle><span>Recurring Event Information</span></BookingsSectionTitle>
                <StyledGridRow gutter={16}>

                  <StyledGridCol span={4}>
                    <SecureFormItem
                      fieldName="startDate"
                      formItemProps={{
                        name: "startDate", label: "Start Date",
                        rules: !fullSearch ? [{ required: true, message: "You must enter the start date" }] : [],
                      }}
                    >
                      <CommonDatePicker format="MM/DD/YYYY" placeholder="MM/DD/YYYY" showTime={false} />
                    </SecureFormItem>
                  </StyledGridCol>

                  <StyledGridCol span={4}>
                    <SecureFormItem
                      fieldName="endDate"
                      formItemProps={{ name: "endDate", label: "End Date" }}
                    >
                      <CommonDatePicker format="MM/DD/YYYY" placeholder="MM/DD/YYYY" showTime={false} />
                    </SecureFormItem>
                  </StyledGridCol>

                  <StyledGridCol span={5}>
                    <SecureFormItem
                      fieldName="activeRecurring"
                      formItemProps={{
                        name: "activeRecurring", label: "Active?",
                        layout: "horizontal", labelPostion: "label-right",
                        style: { paddingTop: "25px" }, valuePropName: "checked",
                      }}
                    >
                      <Checkbox />
                    </SecureFormItem>
                  </StyledGridCol>

                  {/* Reactivation fields — overrideDisabled when recurring is active */}
                  {!isActiveRecurring && (
                    <>
                      <StyledGridCol span={5}>
                        <SecureFormItem
                          fieldName="reactivationDate"
                          overrideDisabled={!!isActiveRecurring}
                          formItemProps={{ name: "reactivationDate", label: "Reactivation Date" }}
                        >
                          <CommonDatePicker format="MM/DD/YYYY" placeholder="MM/DD/YYYY" showTime={false} />
                        </SecureFormItem>
                      </StyledGridCol>
                      <StyledGridCol span={5}>
                        <SecureFormItem
                          fieldName="reactivationComments"
                          overrideDisabled={!!isActiveRecurring}
                          formItemProps={{ name: "reactivationComments", label: "Reactivation Comments" }}
                        >
                          <TextField />
                        </SecureFormItem>
                      </StyledGridCol>
                    </>
                  )}

                </StyledGridRow>

                {/* Day-of-week schedule table */}
                <StyledGridRow gutter={16}>
                  <StyledGridCol>
                    <table cellPadding={10}>
                      <thead>
                        <tr>
                          <th />
                          {["Active", "Start Time", "End Time"].map((h) => (
                            <th key={h}>
                              <StyledTypographyText color="var(--label-color-primary);" fontSize="14px" fontWeight="700">
                                {h}
                              </StyledTypographyText>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {DAYS_OF_WEEK.map((day) => (
                          <tr key={day}>
                            <td>
                              <StyledTypographyText color="var(--text-black);" fontSize="14px" fontWeight="700">
                                {day}
                              </StyledTypographyText>
                            </td>
                            <td>
                              <SecureFormItem
                                fieldName={`${day}_active`}
                                formItemProps={{ name: [day, "active"], valuePropName: "checked" }}
                              >
                                <Checkbox />
                              </SecureFormItem>
                            </td>
                            <td>
                              <SecureFormItem
                                fieldName={`${day}_startTime`}
                                formItemProps={{
                                  name: [day, "startTime"],
                                  rules: !fullSearch
                                    ? [({ getFieldValue }) => ({
                                        validator(_, value) {
                                          const dayData = getFieldValue(day);
                                          if (dayData?.active && !value) return Promise.reject(new Error("You must enter the start time"));
                                          return Promise.resolve();
                                        },
                                      })]
                                    : [],
                                }}
                              >
                                <TimePicker format="HH:mm" />
                              </SecureFormItem>
                            </td>
                            <td>
                              <SecureFormItem
                                fieldName={`${day}_endTime`}
                                formItemProps={{
                                  name: [day, "endTime"],
                                  rules: !fullSearch
                                    ? [({ getFieldValue }) => ({
                                        validator(_, value) {
                                          const dayData = getFieldValue(day);
                                          if (dayData?.active && dayData?.startTime && !value) return Promise.reject(new Error("You must enter the end time"));
                                          return Promise.resolve();
                                        },
                                      })]
                                    : [],
                                }}
                              >
                                <TimePicker format="HH:mm" />
                              </SecureFormItem>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Hidden validator for at-least-one-day rule */}
                    <FormItem
                      hidden
                      name="scheduledDaysValid"
                      rules={!fullSearch ? [{
                        validator: (_, value) => {
                          if (form.getFieldValue("activeRecurring")) {
                            const valid = DAYS_OF_WEEK.some((day) => form.getFieldValue(day)?.active);
                            if (!valid) return Promise.reject(new Error("If this recurring event is Active, then you must enter a schedule for at least one day."));
                          }
                          return Promise.resolve();
                        },
                      }] : []}
                    />
                  </StyledGridCol>
                </StyledGridRow>
              </>
            )}

            {/* ── Other Information ────────────────────────────────── */}
            <BookingsSectionTitle><span>Other Information</span></BookingsSectionTitle>
            <StyledGridRow gutter={16}>
              <StyledGridCol span={24}>
                <SecureFormItem
                  fieldName="comments"
                  formItemProps={{ name: "comments" }}
                >
                  <TextField inputType="textarea" as="textarea" variant="outlined" rows="3" />
                </SecureFormItem>
              </StyledGridCol>
            </StyledGridRow>

            {/* ── Business Details (Job Schedule only) ─────────────── */}
            {isJobSchedule && (
              <>
                <BookingsSectionTitle><span>Business Details</span></BookingsSectionTitle>
                <StyledGridRow gutter={16}>
                  <StyledGridCol span={6}>
                    <SecureFormItem
                      fieldName="businessName"
                      formItemProps={{
                        name: "businessName", label: "Business Name",
                        rules: !fullSearch
                          ? [
                              { required: true, message: "Business Name is required" },
                              { pattern: /^\S.*\S?$/, message: "Business Name cannot have empty spaces" },
                            ]
                          : [],
                      }}
                    >
                      <TextField />
                    </SecureFormItem>
                  </StyledGridCol>
                  <StyledGridCol span={4}>
                    <SecureFormItem
                      fieldName="businessPhone"
                      formItemProps={{ name: "businessPhone", label: "Business Phone" }}
                    >
                      <TextField />
                    </SecureFormItem>
                  </StyledGridCol>
                  <StyledGridCol span={4}>
                    <SecureFormItem
                      fieldName="businessFax"
                      formItemProps={{ name: "businessFax", label: "Business Fax" }}
                    >
                      <TextField />
                    </SecureFormItem>
                  </StyledGridCol>
                </StyledGridRow>

                <StyledGridRow gutter={16}>
                  <StyledGridCol span={6}>
                    <SecureFormItem fieldName="businessHouseNumber" formItemProps={{ name: "businessHouseNumber", label: "#" }}>
                      <TextField />
                    </SecureFormItem>
                  </StyledGridCol>
                  <StyledGridCol span={4}>
                    <SecureFormItem fieldName="businessStreet" formItemProps={{ name: "businessStreet", label: "Street" }}>
                      <TextField />
                    </SecureFormItem>
                  </StyledGridCol>
                </StyledGridRow>

                <StyledGridRow gutter={16}>
                  <StyledGridCol span={6}>
                    <SecureFormItem fieldName="businessApt" formItemProps={{ name: "businessApt", label: "Apt#" }}>
                      <TextField />
                    </SecureFormItem>
                  </StyledGridCol>
                  <StyledGridCol span={4}>
                    <SecureFormItem fieldName="businessStreet2" formItemProps={{ name: "businessStreet2", label: "Street2" }}>
                      <TextField />
                    </SecureFormItem>
                  </StyledGridCol>
                </StyledGridRow>

                <StyledGridRow gutter={16}>
                  <StyledGridCol span={6}>
                    <SecureFormItem fieldName="businessCity" formItemProps={{ name: "businessCity", label: "City" }}>
                      <TextField />
                    </SecureFormItem>
                  </StyledGridCol>
                  <StyledGridCol span={2}>
                    <SecureFormItem fieldName="businessState" formItemProps={{ name: "businessState", label: "ST" }}>
                      <Select options={getOptionsFromRefList("USState")} placeholder="ST" showSearch={false} variant="outlined" />
                    </SecureFormItem>
                  </StyledGridCol>
                  <StyledGridCol span={4}>
                    <SecureFormItem
                      fieldName="businessZip"
                      formItemProps={{
                        name: "businessZip", label: "Zip Code",
                        rules: !fullSearch
                          ? [{ pattern: /^[0-9-]{1,10}$/, message: "The zipcode field allows numbers and dashes only and must be 10 characters or less" }]
                          : [],
                      }}
                    >
                      <TextField />
                    </SecureFormItem>
                  </StyledGridCol>
                </StyledGridRow>

                {businessExtraFields.length > 0 && (
                  <>
                    <BookingsSectionTitle><span>Additional Business Information</span></BookingsSectionTitle>
                    <StyledGridRow gutter={16}>
                      {businessExtraFields.map((sf, index) => (
                        <DynamicForm
                          key={index}
                          formAttributes={sf}
                          form={form}
                          bookingsFieldsEnabled={bookingsFieldsEnabled}
                        />
                      ))}
                    </StyledGridRow>
                  </>
                )}

                <ScheduledEventsInspections
                  inspections={inspections}
                  setInspections={setInspections}
                />
              </>
            )}
          </Form>
        </FieldSecurityProvider>
      </Modal>
    </>
  );
};

export default ScheduledEventsDetails;